/**
 * Phrases Tab Component
 * Displays phrase frequency analysis with sortable table
 */

import { ChangeEvent } from 'react'

import {
	Autocomplete,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from '@mui/material'

import MDBox from 'components/MDBox'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'

import { PhrasesTabProps } from './types'

const TOP_PHRASES_OPTIONS = [10, 15, 20, 30]

function PhrasesTab({
	phrases,
	maxPhraseCount,
	searchFilter,
	onSearchFilterChange,
	topPhrasesCount,
	onTopPhrasesCountChange,
	sortField,
	sortDirection,
	onSort,
}: PhrasesTabProps): JSX.Element {
	return (
		<MDBox>
			<MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<MDTypography variant="h6">Phrase Frequency Analysis</MDTypography>
				<MDBox display="flex" gap={2}>
					<MDInput
						placeholder="Search phrases..."
						size="small"
						value={searchFilter}
						onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchFilterChange(e.target.value)}
						sx={{ width: 200 }}
					/>
					<Autocomplete
						disableClearable
						value={topPhrasesCount}
						options={TOP_PHRASES_OPTIONS}
						getOptionLabel={option => `Top ${option}`}
						onChange={(_, newValue) => onTopPhrasesCountChange(newValue)}
						size="small"
						sx={{ width: 120 }}
						renderInput={params => <MDInput {...params} label="Count" />}
					/>
				</MDBox>
			</MDBox>

			<TableContainer>
				<Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 650 }}>
					<TableHead sx={{ display: 'table-header-group', width: '100%' }}>
						<TableRow sx={{ display: 'table-row', width: '100%' }}>
							<TableCell sx={{ width: '5%' }} align="center">
								#
							</TableCell>
							<TableCell sx={{ width: '35%' }} align="left">
								<TableSortLabel
									active={sortField === 'phrase'}
									direction={sortField === 'phrase' ? sortDirection : 'asc'}
									onClick={() => onSort('phrase')}
								>
									Phrase
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ width: '12%' }} align="center">
								<TableSortLabel
									active={sortField === 'count'}
									direction={sortField === 'count' ? sortDirection : 'asc'}
									onClick={() => onSort('count')}
								>
									Count
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ width: '13%' }} align="center">
								<TableSortLabel
									active={sortField === 'percentage'}
									direction={sortField === 'percentage' ? sortDirection : 'asc'}
									onClick={() => onSort('percentage')}
								>
									Percentage
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ width: '35%' }} align="left">
								Distribution
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{phrases.map((phrase, index) => {
							const barWidth = (phrase.count / maxPhraseCount) * 100
							return (
								<TableRow key={phrase.phrase} hover>
									<TableCell sx={{ width: '5%' }} align="center">
										<MDTypography variant="caption" color="text">
											{index + 1}
										</MDTypography>
									</TableCell>
									<TableCell sx={{ width: '35%' }} align="left">
										<MDTypography variant="button" fontWeight="medium">
											{phrase.phrase}
										</MDTypography>
									</TableCell>
									<TableCell sx={{ width: '12%' }} align="center">
										<Chip label={phrase.count} size="small" color="primary" />
									</TableCell>
									<TableCell sx={{ width: '13%' }} align="center">
										<MDTypography variant="caption">{phrase.percentage?.toFixed(2)}%</MDTypography>
									</TableCell>
									<TableCell sx={{ width: '35%' }} align="left">
										<MDBox
											sx={{
												width: `${barWidth}%`,
												height: 8,
												backgroundColor: 'primary.main',
												borderRadius: 1,
												minWidth: 4,
											}}
										/>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</MDBox>
	)
}

export default PhrasesTab
